// StatsSummaryView — the default summary view body.
//
// Renders the numeric panel (count/sum/avg/min/max for single-period kinds, or
// the current/prior/Change comparison for `period_over_period`) followed by the
// top-categories chip row. A body receives `SummaryViewBodyProps` and owns no
// card chrome, header, or picker.
//
// For period_over_period the comparison is responsive:
//   * Desktop (sm+): a 4-column table — [stat] [current period] [prior period]
//     [Change] — with full stat words and the real period names as headers.
//   * Mobile (<sm): each stat wraps onto two lines — the full stat label, then
//     the indented row of numbers below it — so the numbers get the full width
//     while the label still reads in full.

import React from 'react'
import type { SummaryNumericResponse } from '../../../api/client'
import type { Granularity } from '../../../charts/windowRange'
import type { SummaryViewBodyProps } from '../viewRegistry'
import { periodLabels, type PeriodBounds } from '../periodFormat'

function fmtNum(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—'
  const rounded = Math.round(v * 100) / 100
  return String(rounded)
}

interface StatCellProps {
  label: string
  value: number | null | undefined
}

const StatCell: React.FC<StatCellProps> = ({ label, value }) => (
  <div className="flex flex-col rounded-md bg-gray-50 px-3 py-2">
    <span className="text-xs uppercase tracking-wide text-gray-400">{label}</span>
    <span className="text-lg font-semibold text-gray-800">{fmtNum(value)}</span>
  </div>
)

/** The five scalar stats shared by single-period and period-over-period
 *  payloads. Numeric fields are null when the period had no check-ins. */
interface ScalarStats {
  count?: number | null
  sum?: number | null
  avg?: number | null
  min?: number | null
  max?: number | null
}

/** A signed delta string with a leading +/− (and an en-dash for null), plus a
 *  Tailwind color class: positive → green, negative → red, zero/null → gray. */
function fmtDelta(v: number | null | undefined): { text: string; color: string } {
  if (v === null || v === undefined) return { text: '—', color: 'text-gray-400' }
  const rounded = Math.round(v * 100) / 100
  if (rounded === 0) return { text: '0', color: 'text-gray-400' }
  const magnitude = fmtNum(Math.abs(rounded))
  if (rounded > 0) return { text: `+${magnitude}`, color: 'text-green-600' }
  return { text: `−${magnitude}`, color: 'text-rose-600' }
}

// The five metrics, in the single-period grid's order.
const STAT_METRICS: Array<{ key: keyof ScalarStats; label: string }> = [
  { key: 'count', label: 'Checkins' },
  { key: 'sum', label: 'Total' },
  { key: 'avg', label: 'Average' },
  { key: 'min', label: 'Smallest' },
  { key: 'max', label: 'Largest' },
]

/** Current-vs-prior comparison for period_over_period. The desktop table and
 *  the mobile stacked layout render from the same metric list. */
const ComparisonPanel: React.FC<{
  current: ScalarStats
  prior: ScalarStats
  delta: ScalarStats
  currentLabel: string
  priorLabel: string
}> = ({ current, prior, delta, currentLabel, priorLabel }) => {
  // Descriptive phrases ("week starting May 24", "previous week") read better
  // in sentence case than as shouty uppercase, so the period headers capitalize
  // only the first letter rather than the uppercase/tracking the stat-row labels
  // use. Done here, not in periodFormat, so the chart view's labels are untouched.
  const cap = (s: string) => (s ? s[0].toUpperCase() + s.slice(1) : s)
  const headerCls = 'px-3 py-1.5 text-sm text-gray-400'

  return (
    <div data-testid="summary-comparison">
      {/* Desktop: 4-column table. */}
      <div className="hidden overflow-hidden rounded-md bg-gray-50 sm:block">
        <div className="grid grid-cols-4 gap-px text-right">
          {/* Empty top-left cell (no "Metric" word). */}
          <span className={`${headerCls} text-left`} aria-hidden />
          <span className={headerCls}>{cap(currentLabel)}</span>
          <span className={headerCls}>{cap(priorLabel)}</span>
          <span className={headerCls}>Change</span>
          {STAT_METRICS.map(({ key, label }) => {
            const d = fmtDelta(delta[key])
            return (
              <React.Fragment key={key}>
                <span className="bg-white px-3 py-2 text-left text-xs uppercase tracking-wide text-gray-400">
                  {label}
                </span>
                <span className="bg-white px-3 py-2 text-sm font-semibold text-gray-800">
                  {fmtNum(current[key])}
                </span>
                <span className="bg-white px-3 py-2 text-sm font-semibold text-gray-500">
                  {fmtNum(prior[key])}
                </span>
                <span className={`bg-white px-3 py-2 text-sm font-semibold ${d.color}`}>
                  {d.text}
                </span>
              </React.Fragment>
            )
          })}
        </div>
      </div>

      {/* Mobile: stacked — the full stat label, then the numbers row below it. */}
      <div className="rounded-md bg-gray-50 px-2 py-1 sm:hidden">
        {/* Column header naming the two periods (and the change column). */}
        <div className="grid grid-cols-3 gap-2 px-1 pb-1 text-right text-xs leading-tight text-gray-400">
          <span>{cap(currentLabel)}</span>
          <span>{cap(priorLabel)}</span>
          <span>Change</span>
        </div>
        {STAT_METRICS.map(({ key, label }) => {
          const d = fmtDelta(delta[key])
          return (
            <div key={key} className="border-t border-gray-200/70 py-1.5">
              <span className="text-xs uppercase tracking-wide text-gray-400">
                {label}
              </span>
              <div className="grid grid-cols-3 gap-2 pl-3 text-right tabular-nums">
                <span className="text-base font-semibold text-gray-800">
                  {fmtNum(current[key])}
                </span>
                <span className="text-base font-semibold text-gray-500">
                  {fmtNum(prior[key])}
                </span>
                <span className={`text-base font-semibold ${d.color}`}>{d.text}</span>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

/** Render whatever numeric shape the kind produced. Stats-bearing kinds get the
 *  count/sum/avg/min/max grid; everything else falls back to a couple of
 *  recognizable scalars so the panel is never blank. */
const NumericPanel: React.FC<{
  numeric: SummaryNumericResponse
  granularity: Granularity
}> = ({ numeric, granularity }) => {
  const payload = numeric.numeric_payload || {}

  // period_over_period carries three stat blocks (current/prior/delta) plus the
  // period bounds — render the full comparison. Detect by kind OR by presence
  // of all three blocks.
  const current = payload.current as ScalarStats | undefined
  const prior = payload.prior as ScalarStats | undefined
  const delta = payload.delta as ScalarStats | undefined
  const currentPeriod = payload.current_period as PeriodBounds | undefined
  const priorPeriod = payload.prior_period as PeriodBounds | undefined
  const isComparison =
    payload.kind === 'period_over_period' || (!!current && !!prior && !!delta)
  if (isComparison && current && prior && delta) {
    // Real period names when the bounds are present; otherwise fall back to
    // generic words so the table is never headerless.
    const labels =
      currentPeriod && priorPeriod
        ? periodLabels(currentPeriod, priorPeriod, granularity)
        : { current: 'Current', prior: 'Prior' }
    return (
      <ComparisonPanel
        current={current}
        prior={prior}
        delta={delta}
        currentLabel={labels.current}
        priorLabel={labels.prior}
      />
    )
  }

  const stats = (payload.stats || payload.current) as ScalarStats | undefined

  if (stats) {
    return (
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
        <StatCell label="Checkins" value={stats.count ?? null} />
        <StatCell label="Total" value={stats.sum ?? null} />
        <StatCell label="Average" value={stats.avg ?? null} />
        <StatCell label="Smallest" value={stats.min ?? null} />
        <StatCell label="Largest" value={stats.max ?? null} />
      </div>
    )
  }

  if (typeof payload.active_days === 'number') {
    return (
      <div className="grid grid-cols-3 gap-2">
        <StatCell label="Active days" value={payload.active_days} />
        <StatCell label="Longest streak" value={payload.longest_streak ?? null} />
        <StatCell label="Current streak" value={payload.current_streak ?? null} />
      </div>
    )
  }

  if (typeof payload.checkin_count === 'number') {
    return (
      <div className="grid grid-cols-2 gap-2">
        <StatCell label="Checkins" value={payload.checkin_count} />
      </div>
    )
  }

  return null
}

const TopCategories: React.FC<{ numeric: SummaryNumericResponse }> = ({ numeric }) => {
  const cats = (numeric.numeric_payload?.top_categories || []) as Array<{
    value: any
    count: number
  }>
  if (!cats.length) return null
  return (
    <div className="mt-3 flex flex-wrap gap-1.5">
      {cats.map((c, i) => (
        <span
          key={i}
          className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-0.5 text-xs text-amber-900"
        >
          {String(c.value)}
          <span className="text-amber-600">×{c.count}</span>
        </span>
      ))}
    </div>
  )
}

/** The stats view body: the numeric panel followed by the top-categories chip
 *  row. Uses `granularity` to label the comparison's period columns. */
const StatsSummaryView: React.FC<SummaryViewBodyProps> = ({ numeric, granularity }) => (
  <>
    <NumericPanel numeric={numeric} granularity={granularity} />
    <TopCategories numeric={numeric} />
  </>
)

export default StatsSummaryView
